import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { verificationService } from '../services/verificationService';
import VerificationBadge from '../components/VerificationBadge';
import '../styles/Profile.css';

const Profile = () => {
    const { user, logout } = useAuth();
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        if (user) {
            loadVerificationStatus();
        }
    }, [user]);

    const loadVerificationStatus = async () => {
        try {
            setLoading(true);
            const response = await verificationService.getVerificationStatus();
            if (response.status === 'success') {
                setVerificationStatus(response.data);
            }
        } catch (err) {
            console.error('Error loading verification status:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            logout();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getVerificationLevelColor = (level) => {
        switch (level) {
            case 'none': return '#dc2626';
            case 'basic': return '#d97706';
            case 'enhanced': return '#2563eb';
            case 'full': return '#16a34a';
            default: return '#6b7280';
        }
    };

    if (!user) {
        return <div className="loading">Loading profile...</div>;
    }

    return (
        <div className="profile-page">
            <div className="profile-header">
                <div className="profile-avatar">
                    <div className="avatar-placeholder">
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                </div>
                <div className="profile-info">
                    <h1>{user.name || 'User'}</h1>
                    <p className="user-email">{user.email}</p>
                    <p className="member-since">
                        Member since {formatDate(user.created_at)}
                    </p>
                </div>
            </div>

            <div className="profile-tabs">
                <button 
                    className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={`tab-button ${activeTab === 'verification' ? 'active' : ''}`}
                    onClick={() => setActiveTab('verification')}
                >
                    Verification
                </button>
                <button 
                    className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                >
                    Settings
                </button>
            </div>

            <div className="profile-content">
                {activeTab === 'overview' && (
                    <div className="overview-tab">
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h3>Account Status</h3>
                                <div className="stat-value">
                                    {loading ? (
                                        <span className="loading-text">Loading...</span>
                                    ) : (
                                        <span 
                                            className="status-badge"
                                            style={{ 
                                                backgroundColor: getVerificationLevelColor(verificationStatus?.verification_level) + '20',
                                                color: getVerificationLevelColor(verificationStatus?.verification_level)
                                            }}
                                        >
                                            {verificationStatus?.verification_level?.toUpperCase() || 'NONE'}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="stat-card">
                                <h3>Verification Status</h3>
                                <div className="verification-badges-container">
                                    {loading ? (
                                        <span className="loading-text">Loading...</span>
                                    ) : (
                                        verificationStatus?.verification_status && (
                                            <VerificationBadge 
                                                verificationStatus={verificationStatus.verification_status} 
                                                size="medium" 
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="quick-actions">
                            <h3>Quick Actions</h3>
                            <div className="actions-grid">
                                <Link to="/listings/create" className="action-card">
                                    <div className="action-icon">📝</div>
                                    <h4>Create Listing</h4>
                                    <p>Post a new item for sale</p>
                                </Link>
                                <Link to="/my-listings" className="action-card">
                                    <div className="action-icon">📋</div>
                                    <h4>My Listings</h4>
                                    <p>Manage your listings</p>
                                </Link>
                                <Link to="/my-purchases" className="action-card">
                                    <div className="action-icon">🛒</div>
                                    <h4>My Purchases</h4>
                                    <p>View your purchase history</p>
                                </Link>
                                <Link to="/verification" className="action-card">
                                    <div className="action-icon">✅</div>
                                    <h4>Verification</h4>
                                    <p>Complete your verification</p>
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'verification' && (
                    <div className="verification-tab">
                        <div className="verification-overview">
                            <h3>Verification Status</h3>
                            <p>Complete your verification to build trust with other users and unlock additional features.</p>
                            
                            {loading ? (
                                <div className="loading-state">Loading verification status...</div>
                            ) : (
                                <div className="verification-summary">
                                    <div className="verification-level-display">
                                        <span className="level-label">Current Level:</span>
                                        <span 
                                            className="level-badge"
                                            style={{ 
                                                backgroundColor: getVerificationLevelColor(verificationStatus?.verification_level) + '20',
                                                color: getVerificationLevelColor(verificationStatus?.verification_level)
                                            }}
                                        >
                                            {verificationStatus?.verification_level?.toUpperCase() || 'NONE'}
                                        </span>
                                    </div>
                                    
                                    {verificationStatus?.verification_status && (
                                        <VerificationBadge 
                                            verificationStatus={verificationStatus.verification_status} 
                                            size="large" 
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="verification-cta">
                            <h4>Ready to get verified?</h4>
                            <p>Complete your verification to show other users that you're a trusted member of our community.</p>
                            <Link to="/verification" className="verify-cta-button">
                                Manage Verification
                            </Link>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="settings-tab">
                        <div className="settings-section">
                            <h3>Account Information</h3>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>Name</label>
                                    <span>{user.name || 'Not set'}</span>
                                </div>
                                <div className="info-item">
                                    <label>Email</label>
                                    <span>{user.email}</span>
                                </div>
                                <div className="info-item">
                                    <label>Member Since</label>
                                    <span>{formatDate(user.created_at)}</span>
                                </div>
                                <div className="info-item">
                                    <label>Account Status</label>
                                    <span className="status-active">Active</span>
                                </div>
                            </div>
                        </div>

                        <div className="settings-section">
                            <h3>Account Actions</h3>
                            <div className="action-buttons">
                                <button className="action-button secondary">
                                    Change Password
                                </button>
                                <button className="action-button secondary">
                                    Update Profile
                                </button>
                                <button className="action-button danger" onClick={handleLogout}>
                                    Logout
                                </button>
                            </div>
                        </div>

                        <div className="settings-section">
                            <h3>Preferences</h3>
                            <p>Account preferences and settings will be available soon.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile; 